import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  InputActionMeta,
} from "react-select";
import { Option } from "../../interface/Option";
import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";

interface DoctorOption extends Option {
  doctorName?: string;

  // Business doctor code returned as Doctor_id; not used as the visible ID.
  doctorId?: string;

  // SQL database ID used by validate-all.
  dbId?: number;
  is_active?: boolean | number | string;
  status?: string;
  __inactive?: boolean;
}

interface ListOfDoctorNameProps {
  onChange: (selected: any) => void;
  clear?: boolean;
  defaultValue?: any;
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "list-of-doctor-name";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);

  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

// Always load fresh status information. A module-level cache would keep doctors
// selectable after they have been deactivated elsewhere in the application.
const isInactiveDoctor = (source: any): boolean => {
  const value = source?.is_active ?? source?.Is_active ?? source?.isActive
    ?? source?.status ?? source?.Status;
  if (value === false || value === 0 || value === "0") return true;
  return typeof value === "string" &&
    ["inactive", "disabled", "false"].includes(value.toLowerCase().trim());
};

const doctorKey = (source: any): string => String(
  source?.dbId ?? source?.db_id ?? source?.id ?? source?.value ?? ""
);

const fetchAllDoctors = async (): Promise<DoctorOption[]> => {
  const response = await DoctorAxiosInstance.get("getDoctor");
  const result = response.data?.result ?? response.data?.data ?? [];
  const data: any[] = Array.isArray(result) ? result :
    Array.isArray(result?.data) ? result.data : [];

  return data.map((item: any): DoctorOption | null => {
    const id = item?.id ?? item?.doctor_db_id ?? item?.doctorDbId;
    const businessId = item?.Doctor_id ?? item?.doctor_id ?? item?.doctorId ?? id;
    const name = String(item?.Doctor_name ?? item?.doctor_name ??
      item?.doctorName ?? item?.name ?? "").trim();
    if (id == null || !name) return null;
    return {
      value: String(id),
      label: `(${businessId}) ${name}`,
      doctorName: name,
      doctorId: String(businessId),
      dbId: Number(id),
      is_active: item?.is_active ?? item?.Is_active ?? item?.isActive,
      status: item?.status ?? item?.Status,
      __inactive: isInactiveDoctor(item),
    } as DoctorOption;
  }).filter((item): item is DoctorOption => item !== null);
};

// Merge the authoritative status with a preselected relation reference.
// The full list is used only to resolve status; inactive rows are never offered
// in Add/Search. A known inactive reference cannot become active through a
// value refresh or react-select onChange.
const normalizeDoctorSelection = (defaults: any[], all: DoctorOption[]): DoctorOption[] =>
  defaults.map((raw: any) => {
    const key = doctorKey(raw);
    const businessId = String(raw?.doctorId ?? raw?.Doctor_id ?? raw?.doctor_id ?? "");
    const canonical = all.find((item) => doctorKey(item) === key) ??
      (businessId ? all.find((item) => item.doctorId === businessId) : undefined);
    const selected = canonical ? { ...raw, ...canonical } : { ...raw };
    const inactive = isInactiveDoctor(raw) || Boolean(raw?.__inactive) ||
      Boolean(canonical?.__inactive);
    return {
      ...selected,
      value: String(canonical?.value ?? key),
      label: String(canonical?.label ?? raw?.label ?? raw?.Doctor_name ?? key),
      __inactive: inactive,
    } as DoctorOption;
  });

const ListOfDoctorName = ({
  onChange,
  clear,
  defaultValue,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: ListOfDoctorNameProps) => {
  const [options, setOptions] = useState<DoctorOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DoctorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [allOptions, setAllOptions] = useState<DoctorOption[]>([]);

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadAllOptions = useCallback(async () => {
    setLoading(true);

    try {
      const formattedOptions = await fetchAllDoctors();
      setAllOptions(formattedOptions);
      setOptions(formattedOptions.filter((option) => !option.__inactive));
    } catch (error) {
      console.error("Error loading all doctor options:", error);
      setAllOptions([]);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAllOptions();
  }, [loadAllOptions]);

  // Search is local only. No /searchDoctor call is made while typing.
  useEffect(() => {
    const query = inputValue.trim().toLowerCase();

    if (query.length === 0) {
      setOptions(allOptions.filter((option) => !option.__inactive));
      return;
    }

    // Name-only filtering. ID/code are intentionally not searchable here.
    const filtered = allOptions.filter((option) =>
      !option.__inactive && String(option.doctorName ?? "").toLowerCase().includes(query)
    );

    setOptions(filtered);
  }, [inputValue, allOptions]);

  const handleChange = (selected: MultiValue<DoctorOption>) => {
    const incoming = Array.from(selected);
    // A previously linked inactive doctor must not be removed by x,
    // Backspace/Delete or react-select Clear All.
    const kept = selectedOptions.filter((option) =>
      option.__inactive && !incoming.some((item) => doctorKey(item) === doctorKey(option))
    );
    const merged = normalizeDoctorSelection([...incoming, ...kept], allOptions);
    setSelectedOptions(merged);
    onChange(merged);
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  useEffect(() => {
    if (clear) return;
    const incoming = Array.isArray(defaultValue) ? defaultValue : [];
    setSelectedOptions((previous) => {
      const merged = normalizeDoctorSelection(incoming, allOptions);
      // Keep a locked reference if a parent re-renders a stale defaultValue.
      // A genuinely empty defaultValue on a new form still clears the selection.
      if (incoming.length === 0) return [];
      return merged.map((item) => ({
        ...item,
        __inactive: item.__inactive || Boolean(previous.find(
          (old) => doctorKey(old) === doctorKey(item) && old.__inactive
        )),
      }));
    });
  }, [defaultValue, allOptions, clear]);

  const handleInputChange = (
    nextValue: string,
    actionMeta: InputActionMeta
  ): string => {
    if (actionMeta.action === "input-change") {
      setInputValue(nextValue);
    }

    return nextValue;
  };

  const customStyles: StylesConfig<DoctorOption, true> = {
    multiValue: (styles, { data }) => ({
      ...styles,
      backgroundColor: data.__inactive ? "#d1d5db" : "#007BFF",
      border: data.__inactive ? "1px solid #94a3b8" : undefined,
      color: data.__inactive ? "#334155" : "#FFFFFF",
      borderRadius: "5px",
    }),
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data.__inactive ? "#334155" : "#FFFFFF",
      fontWeight: data.__inactive ? 600 : styles.fontWeight,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      display: data.__inactive ? "none" : styles.display,
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
    option: (styles, { isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#007BFF" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),
  };

  const Menu = (props: any) => {
    const qa = qaAttrs(qaIdPrefix, "menu");

    return (
      <components.Menu
        {...props}
        innerProps={{
          ...(props.innerProps || {}),
          ...qa,
        }}
      />
    );
  };

  const OptionComponent = (props: any) => {
    const optionId = qaSafe(String(props.data?.value ?? "unknown"));
    const qa = qaAttrs(qaIdPrefix, `option-${optionId}`);

    return (
      <components.Option
        {...props}
        innerProps={{
          ...props.innerProps,
          ...qa,
          "data-option-label": props.data?.label ?? "",
          "data-option-value": props.data?.value ?? "",
          "data-doctor-db-id": (props.data as DoctorOption)?.dbId ?? "",
          "data-doctor-id": (props.data as DoctorOption)?.doctorId ?? "",
          "data-doctor-name": (props.data as DoctorOption)?.doctorName ?? "",
        }}
      />
    );
  };

  const MultiValueComponent = (props: any) => {
    const selectedId = qaSafe(String(props.data?.value ?? "unknown"));

    return (
      <div
        {...qaAttrs(qaIdPrefix, `selected-${selectedId}`)}
        data-inactive={Boolean((props.data as DoctorOption)?.__inactive) ? "true" : "false"}
        data-selected-label={props.data?.label ?? ""}
        data-selected-value={props.data?.value ?? ""}
        data-doctor-db-id={(props.data as DoctorOption)?.dbId ?? ""}
        data-doctor-id={(props.data as DoctorOption)?.doctorId ?? ""}
        data-doctor-name={(props.data as DoctorOption)?.doctorName ?? ""}
      >
        <components.MultiValue {...props} />
      </div>
    );
  };

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        captureMenuScroll={true}
        isMulti
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Doctor selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options.filter((option) => !option.__inactive)}
        isClearable={!selectedOptions.some((option) => option.__inactive)}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder="Type to search or select from list..."
        noOptionsMessage={() =>
          loading ? "Loading..." : "No doctors found"
        }
        styles={{
          ...customStyles,
          menu: (base: any) => ({
            ...base,
            zIndex: 999999,
            pointerEvents: "auto",
          }),
          menuList: (base: any) => ({
            ...base,
            maxHeight: 280,
            overflowY: "auto",
            pointerEvents: "auto",
            WebkitOverflowScrolling: "touch",
          }),
        }}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
          MultiValueRemove: (props: any) =>
            (props.data as DoctorOption)?.__inactive ? null :
            <components.MultiValueRemove {...props} />,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default ListOfDoctorName;

